import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { subscribe, getState } from '../data/store.js';
import { getChoice, setTheme, subscribeTheme, CHOICES } from '../theme.js';
import { can } from '../auth/permissions.js';

/** Re-render on any store change. Coarse on purpose — the dataset is small. */
export function useStore() {
  return useSyncExternalStore(subscribe, getState, getState);
}

/**
 * `can('billing.write')` for the signed-in staff member.
 *
 * Returns true for everything when there is no session, which is the local
 * demo: there is no server to refuse anything and no tenant to protect, and a
 * demo that hid features would misrepresent the product.
 */
export function useCan() {
  const state = useStore();
  const role = state.session?.role ?? null;
  return (action) => can(role, action);
}

/**
 * Wraps an action that this role may not take.
 *
 * Renders nothing rather than a disabled control: a greyed-out button invites
 * someone to work out how to enable it, while its absence just means the app
 * is not theirs to use that way. The title is there for the case where the
 * layout leaves an obvious hole.
 */
export function Allowed({ action, children, fallback = null }) {
  const allowed = useCan()(action);
  return allowed ? children : fallback;
}

/**
 * Subscribe to a media query.
 *
 * Layout that only moves in CSS cannot relocate a control from a sidebar into
 * a sheet, so the breakpoint has to be legible to JS as well. Kept in sync with
 * the 900px breakpoint in styles.css by hand — there is one, and it is named
 * here so the next person finds both.
 */
export function useMediaQuery(query) {
  const [matches, setMatches] = useState(
    () => (typeof window === 'undefined' ? false : window.matchMedia(query).matches),
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setMatches(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

export const COMPACT = '(max-width: 900px)';

const THEME_LABEL = { light: 'Light', dark: 'Dark', auto: 'Auto' };

/**
 * Light / Dark / Auto as a segmented control — the native idiom for picking
 * one of a small set, and the same three options Apple offers system-wide.
 */
export function ThemeControl() {
  const [choice, setChoice] = useState(getChoice);
  useEffect(() => subscribeTheme(setChoice), []);

  return (
    <div className="segmented" role="group" aria-label="Appearance">
      {CHOICES.map((c) => (
        <button
          key={c}
          type="button"
          aria-pressed={choice === c}
          onClick={() => setTheme(c)}
        >
          {THEME_LABEL[c]}
        </button>
      ))}
    </div>
  );
}

export function Panel({ title, actions, children, bodyless }) {
  return (
    <section className="panel">
      {(title || actions) && (
        <header className="panel-head">
          {title && <h2>{title}</h2>}
          {actions && <div className="spacer" />}
          {actions}
        </header>
      )}
      {bodyless ? children : <div className="panel-body">{children}</div>}
    </section>
  );
}

export function Tile({ label, value, foot }) {
  return (
    <div className="tile">
      <span className="label">{label}</span>
      <span className="value">{value}</span>
      {foot && <span className="foot">{foot}</span>}
    </div>
  );
}

const STATUS_TONE = {
  active: 'good',
  paid: 'good',
  attended: 'good',
  booked: 'accent',
  waitlisted: 'warn',
  lead: 'warn',
  past_due: 'bad',
  lapsed: 'bad',
  cancelled: 'neutral',
  late_cancelled: 'bad',
  no_show: 'bad',
  frozen: 'warn',
  expired: 'neutral',
};

export function Badge({ children, tone, status }) {
  const resolved = tone || STATUS_TONE[status] || 'neutral';
  // JSX hands multi-part children through as an array, and String([a, b])
  // joins with a comma — which is how "retry sms" rendered as "Retry ,Sms".
  const raw = children ?? status;
  const text = (Array.isArray(raw) ? raw.join('') : String(raw)).replace(/_/g, ' ');
  return <span className={`badge ${resolved}`}>{text}</span>;
}

export function Meter({ value, max }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="meter" title={`${value} of ${max}`}>
      <i className={pct >= 100 ? 'full' : ''} style={{ width: `${pct}%` }} />
    </div>
  );
}

const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled])',
  'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',');

let modalSeq = 0;

/**
 * A modal dialog that actually behaves like one.
 *
 * Escape and backdrop-click were already handled; Tab was not, so keyboard
 * focus walked straight out of the dialog and into the page behind it — the
 * user carries on typing into a form they cannot see, and the thing that
 * looked modal was only visually modal. Focus is moved in on open, cycled
 * inside, and returned to whatever opened the dialog on close.
 */
export function Modal({ title, onClose, children, footer }) {
  const panel = useRef(null);
  const titleId = useRef(`modal-title-${(modalSeq += 1)}`).current;

  useEffect(() => {
    const opener = document.activeElement;
    const node = panel.current;

    const items = () => [...node.querySelectorAll(FOCUSABLE)]
      .filter((el) => el.offsetParent !== null || el === document.activeElement);

    // Prefer a real field over the Close button — opening a dialog and landing
    // on "Close" reads as though the app expects you to give up.
    const first = items().find((el) => !el.matches('button')) || items()[0];
    first?.focus();

    const onKey = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab') return;

      const list = items();
      if (list.length === 0) { e.preventDefault(); return; }
      const edge = e.shiftKey ? list[0] : list[list.length - 1];
      // Also catches focus that has already escaped the dialog.
      if (document.activeElement === edge || !node.contains(document.activeElement)) {
        e.preventDefault();
        (e.shiftKey ? list[list.length - 1] : list[0]).focus();
      }
    };

    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      // Returning focus is what makes a dialog feel like a detour rather than
      // a place you got lost in.
      if (opener instanceof HTMLElement && document.contains(opener)) opener.focus();
    };
  }, [onClose]);

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby={titleId} ref={panel}>
        <header className="panel-head">
          <h2 id={titleId}>{title}</h2>
          <div className="spacer" />
          <button className="small" onClick={onClose} aria-label="Close">Close</button>
        </header>
        <div className="panel-body">{children}</div>
        {footer && (
          <footer className="panel-head" style={{ borderBottom: 0, borderTop: '1px solid var(--line)' }}>
            <div className="spacer" />
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
    </label>
  );
}

export function Empty({ children }) {
  return <p className="empty">{children}</p>;
}

/**
 * "1 class", "3 classes". Small, but a product that says "1 classes" reads as
 * unfinished, and every screen counting something needs the same answer.
 */
export function plural(n, one, many = `${one}es`) {
  return `${n} ${n === 1 ? one : many}`;
}

/** Inline result banner that clears itself. */
export function useFlash(ms = 4000) {
  const [flash, setFlash] = useState(null);
  useEffect(() => {
    if (!flash) return undefined;
    const t = setTimeout(() => setFlash(null), ms);
    return () => clearTimeout(t);
  }, [flash, ms]);
  return [flash, setFlash];
}

/**
 * Result of the last action, as a toast rather than an inline banner.
 *
 * Inline, it pushed the whole page down when it appeared and snapped it back
 * four seconds later — so the row you were about to click moved under your
 * cursor, twice. A toast says the same thing without touching the layout.
 *
 * role="status" so a screen reader announces it without stealing focus.
 */
export function Flash({ flash }) {
  if (!flash) return null;
  return (
    <div className="toast-layer" aria-live="polite" role="status">
      <div className={`toast ${flash.tone}`}>{flash.message}</div>
    </div>
  );
}
